import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, Plus } from 'lucide-react';
import { VideoCreditBalance } from '../../types/videoStudio';

interface Props {
  balance: VideoCreditBalance | null;
  onBuy?: () => void;
  compact?: boolean;
}

const CreditsBadge: React.FC<Props> = ({ balance, onBuy, compact }) => {
  const { t } = useTranslation();
  const available = balance?.available ?? 0;
  const total = balance?.total ?? 0;
  const used = balance?.used ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  // Color shifts as the user runs low
  const tone = available <= 10
    ? 'from-rose-500/20 to-orange-500/20 text-rose-700 dark:text-rose-300 border-rose-500/30'
    : available <= 50
      ? 'from-amber-500/20 to-yellow-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30'
      : 'from-emerald-500/20 to-cyan-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';

  if (compact) {
    return (
      <button
        onClick={onBuy}
        title={t('videoStudio.credits.buy')}
        className={`inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-gradient-to-r ${tone} border backdrop-blur text-sm font-semibold hover:brightness-110 transition whitespace-nowrap`}
      >
        <Coins className="w-4 h-4" />
        <span>{available} {t('videoStudio.credits.creditsUnit')}</span>
        {onBuy && <Plus className="w-3.5 h-3.5 opacity-70" />}
      </button>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-gray-950 border border-gray-200 dark:border-gray-800 p-5">
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-gradient-to-br from-primary-500/20 to-purple-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider font-semibold">
            <Coins className="w-4 h-4" />
            {t('videoStudio.credits.title')}
          </div>
          {onBuy && (
            <button
              onClick={onBuy}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-primary-500/10 text-primary-300 border border-primary-500/30 hover:bg-primary-500/20 transition"
            >
              <Plus className="w-3 h-3" />
              {t('videoStudio.credits.buy')}
            </button>
          )}
        </div>
        <div className="flex items-end gap-2 mb-3">
          <span className="text-4xl font-bold text-white tabular-nums">{available}</span>
          <span className="text-sm text-gray-500 mb-1.5">/ {total}</span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-gray-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all duration-500"
            style={{ width: `${100 - pct}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {balance?.monthly_free
            ? t('videoStudio.credits.monthlyFree', { count: balance.monthly_free })
            : t('videoStudio.credits.buyToGenerate')}
        </p>
      </div>
    </div>
  );
};

export default CreditsBadge;
